import { authenticatedFetch, supabase } from "@/supabase/supabaseClient";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export interface ActivityLog {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, unknown>;
  scope: "tenant" | "platform";
  created_at: string;
  user?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
}

export class ActivityLogService {
  /**
   * Fetch tenant-scoped activity logs
   */
  static async getTenantLogs(
    tenantId: string,
    limit = 50,
    offset = 0
  ): Promise<ActivityLog[]> {
    const params = new URLSearchParams({
      select:
        "id,tenant_id,user_id,action,entity_type,entity_id,details,scope,created_at,user:user_profiles!activity_logs_user_profile_fkey(email,first_name,last_name)",
      tenant_id: `eq.${tenantId}`,
      scope: "eq.tenant",
      order: "created_at.desc",
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const response = await authenticatedFetch(
      `${supabaseUrl}/rest/v1/activity_logs?${params.toString()}`
    );

    if (!response.ok) {
      const error = await response.text();
      console.error("Error fetching tenant logs:", error);
      throw new Error("Failed to fetch activity logs");
    }

    return response.json();
  }

  /**
   * Fetch platform-scoped activity logs
   */
  static async getPlatformLogs(limit = 50, offset = 0): Promise<ActivityLog[]> {
    const params = new URLSearchParams({
      select:
        "id,tenant_id,user_id,action,entity_type,entity_id,details,scope,created_at,user:user_profiles!activity_logs_user_profile_fkey(email,first_name,last_name)",
      scope: "eq.platform",
      order: "created_at.desc",
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const response = await authenticatedFetch(
      `${supabaseUrl}/rest/v1/activity_logs?${params.toString()}`
    );

    if (!response.ok) {
      const error = await response.text();
      console.error("Error fetching platform logs:", error);
      throw new Error("Failed to fetch platform logs");
    }

    return response.json();
  }

  /**
   * Create a tenant-scoped log entry
   */
  static async logTenantAction(
    tenantId: string,
    action: string,
    entityType: string,
    entityId?: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    // Use getSession() instead of getUser() — getUser() revalidates the JWT
    // against the auth server (extra round trip per log call), while
    // getSession() reads the cached session locally. The user_id we write
    // here is informational; the INSERT RLS policy enforces auth.uid().
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user ?? null;

    const response = await authenticatedFetch(
      `${supabaseUrl}/rest/v1/activity_logs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: user?.id ?? null,
          action,
          entity_type: entityType,
          entity_id: entityId || null,
          details: details || {},
          scope: "tenant",
        }),
      }
    );

    if (!response.ok) {
      console.error("Failed to log tenant action:", await response.text());
    }
  }

  /**
   * Create a platform-scoped log entry
   */
  static async logPlatformAction(
    action: string,
    entityType: string,
    entityId?: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    // Use getSession() instead of getUser() — getUser() revalidates the JWT
    // against the auth server (extra round trip per log call), while
    // getSession() reads the cached session locally. The user_id we write
    // here is informational; the INSERT RLS policy enforces auth.uid().
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user ?? null;

    const response = await authenticatedFetch(
      `${supabaseUrl}/rest/v1/activity_logs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          tenant_id: null,
          user_id: user?.id ?? null,
          action,
          entity_type: entityType,
          entity_id: entityId || null,
          details: details || {},
          scope: "platform",
        }),
      }
    );

    if (!response.ok) {
      console.error("Failed to log platform action:", await response.text());
    }
  }
}
